import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import SystemConfig, { CONFIG_PARAMS } from "./SystemConfig";
import Wallet, { WALLET_EVENTS } from "../wallet/Wallet";
import AbstractAudio from "../engine/AbstractAudio";
import StatesManager from "../states/StatesManager";
import events from "../events/EventsObservable";
import { EVENTS } from "../events/baseEvents";
// import { ACTION_EVENTS } from '../../ui/UserAction'
// import { GAME_EVENTS } from '../../game/gameEvents'
import PostMessages from "./PostMessages";
import { GAME_EVENTS } from "../game/gameEvents";
import { ACTION_EVENTS } from "../ui/UserAction";

// feature definitions object
const features = {
  basic: {
    game: ["g2wInitializationRequest", "g2wCloseGameFrameCommand"],
    wrapper: ["w2gInitializationResponse"],
  },
  pause: {
    game: ["g2wPauseNotification"],
    wrapper: ["w2gPauseCommand"],
  },
  autoplay: {
    game: ["g2wAutoplayStartNotification", "g2wAutoplayEndNotification"],
    wrapper: ["w2gInterruptGameplayCommand"],
  },
  sound: {
    game: ["g2wSoundUpdateNotification"],
    wrapper: ["w2gSoundUpdateNotification"],
  },
  openpage: {
    game: ["g2wOpenPageCommand"],
    wrapper: [],
  },
  switchgame: {
    game: ["g2wSwitchGameCommand"],
    wrapper: [],
  },
  switchmode: {
    game: ["g2wPlayForRealCommand"],
    wrapper: [],
  },
  loadingstatus: {
    game: [
      "g2wLoadingStartNotification",
      "g2wLoadingProgressNotification",
      "g2wLoadingEndNotification",
      "g2wGameReadyNotification",
    ],
    wrapper: [],
  },
  errors: {
    game: ["g2wErrorNotification"],
    wrapper: [],
  },
  gameround: {
    game: ["g2wGameRoundStartNotification", "g2wGameRoundEndNotification"],
    wrapper: [],
  },
  balanceupdate: {
    game: ["g2wBalanceUpdateNotification"],
    wrapper: [],
  },
  balancerefresh: {
    game: [],
    wrapper: ["w2gRefreshBalanceCommand"],
  },
  betupdate: {
    game: ["g2wBetUpdateNotification"],
    wrapper: [],
  },
  bettingperiod: {
    game: [
      "g2wBettingPeriodStartNotification",
      "g2wBettingPeriodEndtNotification",
    ],
    wrapper: [],
  },
} as const;

//#region type helpers
type getDotted<T extends any[], Acc extends string = ""> = T extends [
  infer Key,
  ...infer Rest,
]
  ? getDotted<
      Rest,
      `${Acc extends "" ? "" : `${Acc}.`}${Key extends string ? Key : ""}`
    >
  : Acc;

type GetKeyed<T, Path extends any[] = ["ucip"]> = T extends readonly string[]
  ? getDotted<[...Path, T[number]]>
  : T extends object
    ? {
        [K in keyof T]: K extends "game" | "wrapper"
          ? GetKeyed<T[K], Path>
          : GetKeyed<T[K], [...Path, K]>;
      }[keyof T]
    : getDotted<Path>;

type AllKeys = GetKeyed<typeof features>;
type ExtractPrefixed<
  TPrefix extends string,
  T extends string = AllKeys,
> = T extends `${TPrefix}${string}` ? T : never;
type G2wMessageType = ExtractPrefixed<`ucip.${keyof typeof features}.g2w`>;
type W2gMessageType = ExtractPrefixed<`ucip.${keyof typeof features}.w2g`>;

type SplitWays<
  T extends { _type: ExtractPrefixed<`ucip.${keyof typeof features}`> },
> =
  | (Extract<G2wMessageType, T["_type"]> extends never
      ? never
      : { _type: Extract<G2wMessageType, T["_type"]> } & {
          [K in Exclude<keyof T, "_type">]: T[K];
        })
  | (Extract<W2gMessageType, T["_type"]> extends never
      ? never
      : { _type: Extract<W2gMessageType, T["_type"]> } & {
          [K in Exclude<keyof T, "_type">]: T[K];
        });
type SoundInitConfig = ["sound", Omit<SoundMessage, "_type">];
type OpenpageInitConfig = [
  "openpage",
  {
    delegatePages: (
      | "deposit"
      | "responsible_gaming"
      | "help"
      | "support"
      | "game_history"
    )[];
  },
];

//#endregion

//#region message typing
type SwitchmodeMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.switchmode">;
}>;
type GameroundMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.gameround">;
}>;
type BalancerefreshMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.balancerefresh">;
}>;
type BettingperiodMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.bettingperiod">;
}>;
type AutoplayMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.autoplay">;
}>;
type CloseGameMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.basic.g2wCloseGameFrameCommand">;
}>;
type InitRequestMessage = {
  _type: "ucip.basic.g2wInitializationRequest";
  version: "1.0.0";
  features: Exclude<keyof typeof features, "basic">[];
};
type InitResponseMessage = {
  _type: "ucip.basic.w2gInitializationResponse";
  version: "1.0.0";
  features: (
    | Exclude<keyof typeof features, "basic">
    | SoundInitConfig
    | OpenpageInitConfig
  )[];
};
type PauseGameMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.pause">;
  pause: boolean;
}>;
type SoundMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.sound">;
  mute: boolean;
  level: number;
}>;
type OpenpageMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.openpage">;
  pageID: string;
  url: string;
}>;
type SwitchgameMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.switchgame">;
  gameCode: string;
}>;
type LoadingstatusMessage =
  | SplitWays<{
      _type: Exclude<
        ExtractPrefixed<"ucip.loadingstatus">,
        "ucip.loadingstatus.g2wLoadingProgressNotification"
      >;
    }>
  | SplitWays<{
      _type: "ucip.loadingstatus.g2wLoadingProgressNotification";
      progress: number;
    }>;
type ErrorsMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.errors">;
  error: {
    type:
      | "GENERIC_ERROR"
      | "AUTHENTICATION_ERROR"
      | "CONNECTION_ERROR"
      | "INSUFFICIENT_FUNDS";
    description: string;
  };
}>;
type BalanceupdateMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.balanceupdate">;
  balance: {
    value: number;
    winAmount: number;
  };
}>;
type BetupdateMessage = SplitWays<{
  _type: ExtractPrefixed<"ucip.betupdate">;
  totalBet: number;
}>;
type AllMessages =
  | InitRequestMessage
  | InitResponseMessage
  | SwitchmodeMessage
  | GameroundMessage
  | BalancerefreshMessage
  | BettingperiodMessage
  | PauseGameMessage
  | AutoplayMessage
  | CloseGameMessage
  | SoundMessage
  | OpenpageMessage
  | SwitchgameMessage
  | LoadingstatusMessage
  | ErrorsMessage
  | BalanceupdateMessage
  | BetupdateMessage;
//#endregion

type G2WMessage = Extract<AllMessages, { _type: G2wMessageType }>;
type W2GMessage = Extract<AllMessages, { _type: W2gMessageType }>;

@injectable()
export default class PlaytechPostMessages extends PostMessages {
  private supportedFeatures: Exclude<keyof typeof features, "basic">[] = [
    "autoplay",
    "pause",
    "balancerefresh",
  ];
  private allowedFeatures: Exclude<keyof typeof features, "basic">[] = [];

  constructor(
    @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
    @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
    @inject(SERVICE_IDENTIFIER.EngineAudio) audio: AbstractAudio,
    @inject(SERVICE_IDENTIFIER.StatesManager) states: StatesManager,
  ) {
    super(config, wallet, audio, states);
  }

  async init(): Promise<void> {
    if (this.isActive()) {
      // Start listen to postMessages from parent
      window.addEventListener("message", (message) => {
        try {
          const data =
            typeof message.data === "string"
              ? JSON.parse(message.data)
              : message.data;
          this.handleMessage(data);
        } catch (error) {
          console.error("Error parsing postmessage data", error);
        }
      });

      this.postMessage({
        _type: "ucip.basic.g2wInitializationRequest",
        version: "1.0.0",
        features: this.supportedFeatures,
      });
      events.subscribe(EVENTS.SEND_CLOSE_MSG, () => {
        this.postMessage({
          _type: "ucip.basic.g2wCloseGameFrameCommand",
        });
      });
    }
  }

  isActive(): boolean {
    return this.active && this.config.get(CONFIG_PARAMS.INTEGRATION) === "ucip";
  }
  isFeatureAllowed(type: AllKeys): boolean {
    const feature = type.split(".")[1] as keyof typeof features;
    if (feature === "basic") return true;
    return this.allowedFeatures.includes(feature);
  }

  showErrors(): boolean {
    return true;
  }

  //@ts-expect-error ts complains about argument mismatch, but this is internal method, idc about it here
  postMessage(message: G2WMessage) {
    window.parent.postMessage(JSON.stringify(message), "*");
  }

  handleMessage(message: W2GMessage) {
    if (message._type == undefined) return;
    if (!this.isFeatureAllowed(message._type)) return;
    switch (message._type) {
      case "ucip.basic.w2gInitializationResponse":
        this.initFeatures(message);
        break;
      case "ucip.pause.w2gPauseCommand":
        this.handlePauseGame(message);
        break;
      case "ucip.autoplay.w2gInterruptGameplayCommand":
        this.handlePauseAutostart(message);
        break;
      case "ucip.sound.w2gSoundUpdateNotification":
        this.handleSoundChange(message);
        break;
      case "ucip.balancerefresh.w2gRefreshBalanceCommand":
        this.handleRefreshBalance(message);
        break;
      default:
        break;
    }
  }
  initFeatures(msg: InitResponseMessage) {
    for (const feature of msg.features) {
      if (typeof feature === "string") {
        if (!this.supportedFeatures.includes(feature)) continue;
        this.allowedFeatures.push(feature);
        continue;
      }
      if (!this.supportedFeatures.includes(feature[0])) continue;
      this.allowedFeatures.push(feature[0]);
      switch (feature[0]) {
        case "sound":
          this.handleSoundChange({
            _type: "ucip.sound.w2gSoundUpdateNotification",
            ...feature[1],
          });
          break;
        case "openpage":
          break;
      }
    }
    for (const feature of this.allowedFeatures) {
      switch (feature) {
        case "pause":
          this.registerPauseListeners();
          break;
        case "autoplay":
          this.registerAutoplayListeners();
          break;
        case "sound":
          this.registerSoundListeners();
          break;
        case "openpage":
          this.registerOpenpageListeners();
          break;
        case "switchgame":
          this.registerSwitchgameListeners();
          break;
        case "switchmode":
          this.registerSwitchmodeListeners();
          break;
        case "loadingstatus":
          this.registerLoadingstatusListeners();
          break;
        case "errors":
          this.registerErrorsListeners();
          break;
        case "gameround":
          this.registerGameroundListeners();
          break;
        case "balanceupdate":
          this.registerBalanceupdateListeners();
          break;
        case "balancerefresh":
          this.registerBalancerefreshListeners();
          break;
        case "betupdate":
          this.registerBetupdateListeners();
          break;
        case "bettingperiod":
          this.registerBettingperiodListeners();
          break;
      }
    }
  }
  //#region W2G handlers
  handlePauseGame(message: PauseGameMessage) {
    events.dispatch(GAME_EVENTS.REQUEST_PAUSE_GAME, message.pause);
  }
  handlePauseAutostart(message: AutoplayMessage) {
    events.dispatch(ACTION_EVENTS.ACTION_AUTOSTART_STOP);
  }

  handleSoundChange(message: SoundMessage) {}
  handleRefreshBalance(message: BalancerefreshMessage) {
    this.wallet.reloadBalance();
  }
  //#endregion

  //#region G2W lsteners
  registerPauseListeners() {
    events.subscribe(GAME_EVENTS.GAME_PAUSED, (_, data) => {
      this.postMessage({
        _type: "ucip.pause.g2wPauseNotification",
        pause: data,
      });
    });
  }
  registerAutoplayListeners() {
    events.subscribe(GAME_EVENTS.GAME_AUTOSTART_START, (_, data) => {
      this.postMessage({
        _type: "ucip.autoplay.g2wAutoplayStartNotification",
      });
    });
    events.subscribe(GAME_EVENTS.GAME_AUTOSTART_STOP, (_, data) => {
      this.postMessage({
        _type: "ucip.autoplay.g2wAutoplayEndNotification",
      });
    });
  }

  registerLoadingstatusListeners() {}
  registerSoundListeners() {}
  registerOpenpageListeners() {}
  registerSwitchgameListeners() {}
  registerSwitchmodeListeners() {}
  registerErrorsListeners() {}
  registerGameroundListeners() {}
  registerBalanceupdateListeners() {}
  registerBalancerefreshListeners() {}
  registerBetupdateListeners() {}
  registerBettingperiodListeners() {}
  //#endregion
}
